import React from 'react';
import { useSelector } from 'react-redux';
import {
  isOrgAdmin,
  isInitiativeAdmin,
  isInitiativeOrganizer,
  isInitiativeManager,
  isInitiativeAssignee,
  isAuxiliaryTaskSpecialist,
  isPetitionImageViewer,
  isPetitionUploader,
  isReportViewer,
  isCanvassingOrganizationUser,
} from '../helpers/auth';

const useGlobalSelectionsContext = () => {
  const {
    initiativeId,
    organizationId,
    refetchCurrentPageData,
    initiativeSettings,
    adminMode,
    isMainDataLoaded,
    organizations,
    bodyPageOnly,
    initiatives,
  } = useSelector((state) => state.globalSelections);

  const { user } = useSelector((state) => state.auth.auth);

  const numInitiativeId = Number(initiativeId);
  const initiative = initiatives.find(({ id }) => id === numInitiativeId);
  const jurisdictionTimezone = initiative?.jurisdiction?.timezone;
  const jurisdictionState = initiative?.jurisdiction?.state;
  const isSuperAdmin = user?.is_super_admin;
  const isOrganizationAdmin = isOrgAdmin(user?.organization_role);
  const isInitAdmin = isInitiativeAdmin(user?.initiative_role);
  const isInitOrganaizer = isInitiativeOrganizer(user?.initiative_role);
  const isInitManager = isInitiativeManager(user?.initiative_role);
  const isInitAssignee = isInitiativeAssignee(user?.initiative_role);
  const isInitAuxiliaryTaskSpecialist = isAuxiliaryTaskSpecialist(user?.initiative_role);
  const isPetitionImageViewerUser = isPetitionImageViewer(user?.role);
  const isPetitionUploaderUser = isPetitionUploader(user?.role);
  const isAuxiliaryTaskSpecialistUser = isAuxiliaryTaskSpecialist(user?.role);
  const isReportViewerUser = isReportViewer(user?.role);
  const isCanvassingOrganizationUserUser = isCanvassingOrganizationUser(user?.role);

  const isInitiativeEmpty = React.useMemo(
    () => (adminMode ? !adminMode : !numInitiativeId),
    // eslint-disable-next-line
    [initiativeId, adminMode],
  );

  const isOrganizationEmpty = React.useMemo(() => !organizationId, [organizationId]);

  const hideOrganizationsSelect = React.useMemo(
    () => adminMode || !isSuperAdmin || organizations.length === 0,
    [adminMode, isSuperAdmin, organizations],
  );

  const currentOrganizationStages = React.useMemo(() => {
    const currentOrganization = initiative?.organizations?.find((i) => +i.id === +organizationId);
    if (!currentOrganization) return {};
    return {
      stage_1: currentOrganization.pivot.stage_1,
      stage_2: currentOrganization.pivot.stage_2,
      stage_3: currentOrganization.pivot.stage_3,
      stage_4: currentOrganization.pivot.stage_4,
      stage_5: currentOrganization.pivot.stage_5,
    };
    // eslint-disable-next-line
  }, [initiativeId, organizationId]);

  const initiativeSignatoryFields = React.useMemo(() => {
    const fields = initiative?.signatory_settings
      ? [...initiative.signatory_settings.filter((f) => f.is_active)]
      : [];
    return fields.sort((a, b) => a.order - b.order);
    // eslint-disable-next-line
  }, [initiativeId]);

  const customSuggestedMatchFields = React.useMemo(() => {
    const fields = initiative?.custom_suggested_match_fields
      ? [...initiative.custom_suggested_match_fields]
      : [];
    return fields.sort((a, b) => a.order - b.order);
    // eslint-disable-next-line
  }, [initiativeId]);

  const currentOrganizationSettings = React.useMemo(() => {
    const currentOrganization = initiative?.organizations?.find((i) => +i.id === +organizationId);
    if (!currentOrganization) return {};
    return {
      ...currentOrganization.pivot,
      petition_review_checklist_items: [
        ...currentOrganization.pivot.petition_review_checklist_items,
      ].sort((a, b) => (b.order < a.order ? 1 : -1)),
      signature_review_checklist_items: [
        ...currentOrganization.pivot.signature_review_checklist_items,
      ].sort((a, b) => (b.order < a.order ? 1 : -1)),
      petition_field_report_checklist_items: [
        ...currentOrganization.pivot.petition_field_report_checklist_items,
      ].sort((a, b) => (b.order < a.order ? 1 : -1)),
    };
    // eslint-disable-next-line
  }, [initiativeId, organizationId, initiative]);

  return {
    user,
    initiativeSettings,
    initiativeId,
    organizationId,
    isOrganizationEmpty,
    isInitiativeEmpty,
    adminMode,
    isSuperAdmin,
    isOrganizationAdmin,
    isInitAdmin,
    isInitOrganaizer,
    isInitManager,
    isInitAssignee,
    isInitAuxiliaryTaskSpecialist,
    isPetitionImageViewerUser,
    isPetitionUploaderUser,
    isCanvassingOrganizationUserUser,
    isReportViewerUser,
    isAuxiliaryTaskSpecialistUser,
    refetchCurrentPageData,
    isMainDataLoaded,
    hideOrganizationsSelect,
    bodyPageOnly: !!bodyPageOnly,
    initiative,
    jurisdictionTimezone,
    jurisdictionState,
    organizations,
    currentOrganizationStages,
    initiativeSignatoryFields,
    customSuggestedMatchFields,
    currentOrganizationSettings,
    userInitiativeRole: user?.initiative_role,
  };
};

export default useGlobalSelectionsContext;
